import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar, MapPin, Users, Plus, Clock, Trash2, CheckCircle, Sparkles, ArrowRight, Image as ImageIcon, X, UserPlus, List, CreditCard, Mic, FileText, ShieldCheck, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#00e5ff";

const TYPE_STYLES = {
  "Vortrag":              { hex: "#00e5ff", label: "Vortrag" },
  "Deep Dive":            { hex: "#a855f7", label: "Deep Dive" },
  "Deep Dive Pro":        { hex: "#a855f7", label: "Deep Dive (Pro)" },
  "Festival":             { hex: "#ec4899", label: "Festival" },
  "Workshop":             { hex: "#f39c12", label: "Workshop" },
  "Hackathon":            { hex: "#ef4444", label: "Hackathon" },
  "MeetUp":               { hex: "#2ecc71", label: "MeetUp" },
  "Netzwerk Treffen":     { hex: "#14b8a6", label: "Netzwerk Treffen" },
  "Freizeitveranstaltung":{ hex: "#eab308", label: "Freizeit" },
};

const inputCls = "mt-2 bg-black border-white/10 text-white rounded-none h-11 text-[14px] focus:border-[#00e5ff]/60 focus:ring-0 transition-colors";
const formatEUR = (cents) => `${(Number(cents || 0) / 100).toFixed(2).replace(".", ",")} €`;

function FieldLabel({ children }) {
  return <Label className="text-white/50 text-[11px] uppercase tracking-[0.2em] font-outfit font-medium">{children}</Label>;
}

function FlyerUploader({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { setErr("Datei zu groß (max 20 MB)"); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads/event-flyer`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.flyer_url || "");
    } catch (e) { setErr(e?.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <FieldLabel>Flyer / Titelbild (optional, max 20 MB)</FieldLabel>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="event-flyer-file" />
      {value ? (
        <div className="mt-2 relative group border border-white/10">
          <img src={`${process.env.REACT_APP_BACKEND_URL}${value}`} alt="Flyer" className="w-full max-h-56 object-cover" />
          <button type="button" onClick={() => onChange("")} data-testid="event-flyer-remove" className="absolute top-2 right-2 bg-black/70 text-white hover:bg-red-500/80 px-2 py-1 text-xs flex items-center gap-1"><X size={12} /> Entfernen</button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="event-flyer-btn" className="mt-2 w-full h-24 border border-dashed border-white/15 hover:border-[#00e5ff]/50 text-white/50 hover:text-[#00e5ff] transition-colors flex flex-col items-center justify-center gap-1 text-[13px]">
          <ImageIcon size={18} />
          {busy ? "Lade hoch..." : "Flyer hochladen"}
        </button>
      )}
      {err && <p className="text-[12px] text-red-400 mt-1">{err}</p>}
    </div>
  );
}

/* =========================================================
 *  Dialog: Free Register (guests + AGB)
 * ========================================================= */
function RegisterDialog({ ev, open, onOpenChange, onConfirm, user }) {
  const [guests, setGuests] = useState([]);
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const addGuest = () => {
    if (guests.length < 1) {
      const gid = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `g-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setGuests([...guests, { _k: gid, first_name: "", last_name: "", email: "" }]);
    }
  };
  const updGuest = (i, k, v) => setGuests(guests.map((g, idx) => idx === i ? { ...g, [k]: v } : g));
  const rmGuest = (i) => setGuests(guests.filter((_, idx) => idx !== i));
  const submit = async (e) => {
    e.preventDefault();
    if (!terms) { setErr("Bitte Teilnahmebedingungen akzeptieren."); return; }
    // Email validation: not equal to own email
    const myEmail = (user?.email || "").toLowerCase();
    for (const g of guests) {
      if ((g.email || "").toLowerCase() === myEmail && myEmail) {
        setErr("Die Gast-E-Mail darf nicht deine eigene sein.");
        return;
      }
    }
    setSubmitting(true); setErr("");
    try {
      const cleaned = guests.filter(g => g.first_name && g.last_name && g.email).map(({ _k, ...rest }) => rest);
      await onConfirm(cleaned, terms);
      setGuests([]); setTerms(false);
    } catch (e) { setErr(e?.response?.data?.detail || "Anmeldung fehlgeschlagen"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setGuests([]); setTerms(false); setErr(""); } onOpenChange(v); }}>
      <DialogContent className="bg-transparent border-0 text-white rounded-none max-w-lg p-0 shadow-none max-h-[90vh] overflow-y-auto" data-testid="event-register-dialog">
        <div className="relative">
          <div className="absolute -inset-px pointer-events-none" style={{ background: `linear-gradient(135deg, ${ACCENT}40 0%, transparent 50%, ${ACCENT}20 100%)` }} />
          <div className="relative bg-[#0a0a0a] border border-white/10 p-6 sm:p-8">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-px bg-[#00e5ff]/60" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit font-semibold">Anmeldung</span>
              </div>
              <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">{ev?.title}</DialogTitle>
              <DialogDescription className="text-[13px] text-white/45 mt-1">Du meldest dich verbindlich an. Du kannst einen Gast mitbringen (optional).</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-5">
              <div className="border-l-2 border-[#00e5ff]/40 pl-3 text-[13px] text-white/60">
                <span className="text-white/90 font-semibold">Du {guests.length ? "+ 1 Gast" : ""}</span> = {1 + guests.length} {1 + guests.length === 1 ? "Platz" : "Plätze"}
              </div>
              {ev && ev.max_attendees && ev.attendee_count + 1 + guests.length > ev.max_attendees && (
                <div className="border-l-2 border-amber-400/60 bg-amber-400/5 p-3 text-[13px]">
                  <p className="text-amber-300 font-outfit font-semibold">Event ist voll</p>
                  <p className="text-amber-200/70 mt-1 leading-snug">Du kommst auf die <b>Warteliste</b>. Wenn jemand absagt, rückst du automatisch nach und bekommst eine E-Mail.</p>
                </div>
              )}
              {guests.map((g, i) => (
                <div key={g._k} className="border border-white/10 p-3 space-y-2 relative bg-white/[0.015]" data-testid={`guest-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#00e5ff]/70 font-outfit font-semibold">Gast {i+1}</span>
                    <button type="button" onClick={() => rmGuest(i)} data-testid={`guest-remove-${i}`} className="text-white/40 hover:text-red-400"><X size={14} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input required placeholder="Vorname" value={g.first_name} onChange={(e) => updGuest(i, "first_name", e.target.value)} data-testid={`guest-firstname-${i}`} className={inputCls.replace("mt-2", "mt-0")} />
                    <Input required placeholder="Nachname" value={g.last_name} onChange={(e) => updGuest(i, "last_name", e.target.value)} data-testid={`guest-lastname-${i}`} className={inputCls.replace("mt-2", "mt-0")} />
                  </div>
                  <Input required type="email" placeholder="E-Mail" value={g.email} onChange={(e) => updGuest(i, "email", e.target.value)} data-testid={`guest-email-${i}`} className={inputCls.replace("mt-2", "mt-0")} />
                </div>
              ))}
              {guests.length < 1 && (
                <button type="button" onClick={addGuest} data-testid="add-guest-btn" className="w-full h-10 border border-dashed border-white/15 hover:border-[#00e5ff]/50 text-white/50 hover:text-[#00e5ff] transition-colors flex items-center justify-center gap-2 text-[13px] font-outfit">
                  <UserPlus size={14} /> Gast hinzufügen
                </button>
              )}
              <label className="flex items-start gap-3 p-3 border border-white/10 bg-white/[0.02] cursor-pointer" data-testid="terms-free-label">
                <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 w-4 h-4 accent-[#00e5ff] cursor-pointer" data-testid="terms-free-checkbox" />
                <span className="text-[13px] text-white/70 leading-snug">Ich akzeptiere die <a href="/legal" target="_blank" rel="noreferrer" className="text-[#00e5ff] hover:underline">Teilnahmebedingungen</a> und den Datenschutz.</span>
              </label>
              {err && <p className="text-[13px] text-red-400">{err}</p>}
              <Button type="submit" disabled={submitting || !terms} data-testid="confirm-register-btn" className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 text-[14px] tracking-wide transition-all disabled:opacity-40">
                {submitting ? "..." : `Anmeldung bestätigen${guests.length ? ` (${1 + guests.length} Plätze)` : ""}`}
              </Button>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================
 *  Dialog: Paid Checkout (Stripe)
 * ========================================================= */
function PaidCheckoutDialog({ ev, open, onOpenChange, user }) {
  const [guests, setGuests] = useState([]);
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const addGuest = () => {
    if (guests.length < 1) {
      const gid = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `g-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setGuests([...guests, { _k: gid, first_name: "", last_name: "", email: "" }]);
    }
  };
  const updGuest = (i, k, v) => setGuests(guests.map((g, idx) => idx === i ? { ...g, [k]: v } : g));
  const rmGuest = (i) => setGuests(guests.filter((_, idx) => idx !== i));
  const seats = 1 + guests.length;
  const total = (ev?.price_cents || 0) * seats;
  const submit = async (e) => {
    e.preventDefault();
    if (!terms) { setErr("Bitte AGB akzeptieren."); return; }
    const myEmail = (user?.email || "").toLowerCase();
    for (const g of guests) {
      if ((g.email || "").toLowerCase() === myEmail && myEmail) {
        setErr("Die Gast-E-Mail darf nicht deine eigene sein.");
        return;
      }
    }
    setSubmitting(true); setErr("");
    try {
      const cleaned = guests.filter(g => g.first_name && g.last_name && g.email).map(({ _k, ...rest }) => rest);
      const { data } = await axios.post(
        `${API}/payments/event/${ev.id}/checkout`,
        { origin_url: window.location.origin, guests: cleaned, accepted_terms: true },
        { withCredentials: true }
      );
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("Keine Checkout-URL erhalten");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Checkout fehlgeschlagen");
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setGuests([]); setTerms(false); setErr(""); } onOpenChange(v); }}>
      <DialogContent className="bg-transparent border-0 text-white rounded-none max-w-lg p-0 shadow-none max-h-[90vh] overflow-y-auto" data-testid="paid-checkout-dialog">
        <div className="relative">
          <div className="absolute -inset-px pointer-events-none" style={{ background: `linear-gradient(135deg, #f59e0b40 0%, transparent 50%, ${ACCENT}20 100%)` }} />
          <div className="relative bg-[#0a0a0a] border border-white/10 p-6 sm:p-8">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                <CreditCard size={13} className="text-amber-400" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-amber-400/90 font-outfit font-semibold">Kostenpflichtig · Stripe Checkout</span>
              </div>
              <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">{ev?.title}</DialogTitle>
              <DialogDescription className="text-[13px] text-white/45 mt-1">Sichere Zahlung via Stripe. Rechnung wird automatisch per E-Mail zugestellt.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-5">
              <div className="border border-amber-400/30 bg-amber-400/5 p-4 space-y-1">
                <div className="flex items-baseline justify-between text-[13px]">
                  <span className="text-white/70">Einzelpreis</span>
                  <span className="font-outfit font-bold text-white">{formatEUR(ev?.price_cents || 0)}</span>
                </div>
                <div className="flex items-baseline justify-between text-[13px]">
                  <span className="text-white/70">Plätze (Du{guests.length ? " + 1 Gast" : ""})</span>
                  <span className="font-outfit font-bold text-white">× {seats}</span>
                </div>
                <div className="flex items-baseline justify-between pt-2 border-t border-amber-400/20 mt-2">
                  <span className="text-white font-outfit font-semibold text-[14px]">Gesamt (inkl. USt.)</span>
                  <span className="font-outfit font-black text-2xl text-amber-300" data-testid="paid-total">{formatEUR(total)}</span>
                </div>
              </div>
              {guests.map((g, i) => (
                <div key={g._k} className="border border-white/10 p-3 space-y-2 relative bg-white/[0.015]" data-testid={`paid-guest-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#00e5ff]/70 font-outfit font-semibold">Gast {i+1}</span>
                    <button type="button" onClick={() => rmGuest(i)} className="text-white/40 hover:text-red-400"><X size={14} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input required placeholder="Vorname" value={g.first_name} onChange={(e) => updGuest(i, "first_name", e.target.value)} className={inputCls.replace("mt-2", "mt-0")} />
                    <Input required placeholder="Nachname" value={g.last_name} onChange={(e) => updGuest(i, "last_name", e.target.value)} className={inputCls.replace("mt-2", "mt-0")} />
                  </div>
                  <Input required type="email" placeholder="E-Mail" value={g.email} onChange={(e) => updGuest(i, "email", e.target.value)} className={inputCls.replace("mt-2", "mt-0")} />
                </div>
              ))}
              {guests.length < 1 && (
                <button type="button" onClick={addGuest} data-testid="paid-add-guest-btn" className="w-full h-10 border border-dashed border-white/15 hover:border-[#00e5ff]/50 text-white/50 hover:text-[#00e5ff] transition-colors flex items-center justify-center gap-2 text-[13px] font-outfit">
                  <UserPlus size={14} /> Gast hinzufügen (+{formatEUR(ev?.price_cents || 0)})
                </button>
              )}
              <label className="flex items-start gap-3 p-3 border border-amber-400/30 bg-amber-400/[0.03] cursor-pointer" data-testid="paid-terms-label">
                <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 w-4 h-4 accent-[#00e5ff] cursor-pointer" data-testid="paid-terms-checkbox" />
                <span className="text-[13px] text-white/70 leading-snug">
                  Ich akzeptiere die <a href="/legal" target="_blank" rel="noreferrer" className="text-[#00e5ff] hover:underline">AGB &amp; Teilnahmebedingungen</a>.
                  Mir ist bekannt, dass eine <b className="text-white">100 %-Rückerstattung nur bis 5 Kalendertage vor dem Event</b> möglich ist. Danach keine Erstattung.
                </span>
              </label>
              {err && <p className="text-[13px] text-red-400" data-testid="paid-error">{err}</p>}
              <Button type="submit" disabled={submitting || !terms} data-testid="confirm-paid-btn" className="w-full bg-amber-400 text-black hover:bg-amber-400 hover:shadow-[0_0_32px_-4px_rgba(251,191,36,0.7)] rounded-none font-outfit font-semibold h-12 text-[14px] tracking-wide transition-all disabled:opacity-40 inline-flex items-center justify-center gap-2">
                <CreditCard size={16} />
                {submitting ? "Weiterleitung zu Stripe..." : `Kostenpflichtig anmelden · ${formatEUR(total)}`}
              </Button>
              <p className="text-[11px] text-white/35 text-center">Nach der Zahlung erhältst du eine PDF-Rechnung per E-Mail und findest sie jederzeit in deinem Profil.</p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================
 *  Dialog: Deep-Dive Application (Motivation)
 * ========================================================= */
function ApplicationDialog({ ev, open, onOpenChange, onDone }) {
  const [motivation, setMotivation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (motivation.trim().length < 10) { setErr("Bitte mindestens 10 Zeichen."); return; }
    setSubmitting(true); setErr("");
    try {
      await axios.post(`${API}/events/${ev.id}/apply`, { motivation: motivation.trim() }, { withCredentials: true });
      setMotivation("");
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "Bewerbung fehlgeschlagen"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setMotivation(""); setErr(""); } onOpenChange(v); }}>
      <DialogContent className="bg-transparent border-0 text-white rounded-none max-w-lg p-0 shadow-none" data-testid="application-dialog">
        <div className="relative">
          <div className="absolute -inset-px pointer-events-none" style={{ background: `linear-gradient(135deg, #a855f740 0%, transparent 50%, ${ACCENT}20 100%)` }} />
          <div className="relative bg-[#0a0a0a] border border-white/10 p-6 sm:p-8">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                <FileText size={13} className="text-[#a855f7]" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#a855f7]/90 font-outfit font-semibold">Deep Dive · Bewerbung</span>
              </div>
              <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">{ev?.title}</DialogTitle>
              <DialogDescription className="text-[13px] text-white/45 mt-1">
                Dieses Format hat begrenzte Plätze. Erzähl dem Speaker kurz, warum du dabei sein möchtest.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-5">
              <div>
                <FieldLabel>Deine Motivation *</FieldLabel>
                <Textarea required minLength={10} maxLength={1200} value={motivation} onChange={(e) => setMotivation(e.target.value)}
                  placeholder="Was verbindet dich mit dem Thema? Welche Erwartung hast du?"
                  className="mt-2 bg-black border-white/10 text-white rounded-none focus:border-[#a855f7]/60 focus:ring-0 min-h-[140px] text-[14px]"
                  data-testid="motivation-input" />
                <p className="text-[11px] text-white/35 mt-1">{motivation.length}/1200</p>
              </div>
              {err && <p className="text-[13px] text-red-400">{err}</p>}
              <Button type="submit" disabled={submitting} data-testid="submit-application-btn" className="w-full bg-[#a855f7] text-white hover:bg-[#a855f7] hover:shadow-[0_0_32px_-4px_rgba(168,85,247,0.6)] rounded-none font-outfit font-semibold h-11 text-[14px] tracking-wide transition-all">
                {submitting ? "..." : "Bewerbung absenden"}
              </Button>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================
 *  Dialog: Applications Manager (Speaker / Admin)
 * ========================================================= */
function ApplicationsManagerDialog({ ev, open, onOpenChange, onChanged }) {
  const [apps, setApps] = useState(null);
  const [note, setNote] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!ev) return;
    setApps(null); setErr("");
    try {
      const { data } = await axios.get(`${API}/events/${ev.id}/applications`, { withCredentials: true });
      setApps(data);
    } catch (e) {
      setApps([]);
      setErr(e?.response?.data?.detail || "Bewerbungen nicht ladbar");
    }
  }, [ev]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const decide = async (appId, status) => {
    setBusyId(appId); setErr("");
    try {
      await axios.post(`${API}/events/${ev.id}/applications/${appId}/decide`, { status, note: note[appId] || "" }, { withCredentials: true });
      await load();
      onChanged?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Entscheidung fehlgeschlagen"); }
    finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none max-w-2xl p-0 max-h-[85vh] overflow-y-auto" data-testid="applications-dialog">
        <div className="p-6">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Mic size={13} className="text-[#a855f7]" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#a855f7]/90 font-outfit font-semibold">Speaker-Ansicht · Bewerbungen</span>
            </div>
            <DialogTitle className="font-outfit font-bold text-xl text-white tracking-tight">{ev?.title}</DialogTitle>
            <DialogDescription className="text-[13px] text-white/45 mt-1">Entscheide, wer dabei sein darf. Approved Bewerber werden automatisch angemeldet.</DialogDescription>
          </DialogHeader>
          {err && <p className="text-[13px] text-red-400 mt-3">{err}</p>}
          <div className="mt-5 space-y-3">
            {apps === null && <p className="text-white/45 text-sm">Lade...</p>}
            {apps && apps.length === 0 && <p className="text-white/45 text-sm">Noch keine Bewerbungen.</p>}
            {apps && apps.map((a) => {
              const stColor = a.status === "approved" ? "#2ecc71" : a.status === "rejected" ? "#ef4444" : "#f39c12";
              return (
                <div key={a.id} className="border border-white/10 p-4 bg-white/[0.02]" data-testid={`application-row-${a.id}`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-outfit font-semibold text-white">{a.user_name}</p>
                      <p className="text-[12px] text-white/50">{a.user_email}</p>
                    </div>
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-bold border" style={{ color: stColor, borderColor: `${stColor}66`, background: `${stColor}15` }}>
                      {a.status}
                    </span>
                  </div>
                  <p className="mt-3 text-[14px] text-white/80 whitespace-pre-wrap border-l-2 border-[#a855f7]/40 pl-3 leading-relaxed">{a.motivation}</p>
                  {a.status === "pending" && (
                    <div className="mt-4 space-y-2">
                      <Input placeholder="Hinweis an Bewerber (optional)" value={note[a.id] || ""}
                        onChange={(e) => setNote({ ...note, [a.id]: e.target.value })}
                        className={inputCls.replace("mt-2", "mt-0")} data-testid={`application-note-${a.id}`} />
                      <div className="flex gap-2">
                        <Button disabled={busyId === a.id} onClick={() => decide(a.id, "approved")} data-testid={`approve-application-${a.id}`} className="flex-1 bg-[#2ecc71]/10 text-[#2ecc71] hover:bg-[#2ecc71]/20 border border-[#2ecc71]/40 rounded-none h-10 font-outfit font-semibold text-[12px] uppercase tracking-wider">
                          Annehmen
                        </Button>
                        <Button disabled={busyId === a.id} onClick={() => decide(a.id, "rejected")} data-testid={`reject-application-${a.id}`} className="flex-1 bg-[#ef4444]/10 text-[#ef4444] hover:bg-[#ef4444]/20 border border-[#ef4444]/40 rounded-none h-10 font-outfit font-semibold text-[12px] uppercase tracking-wider">
                          Ablehnen
                        </Button>
                      </div>
                    </div>
                  )}
                  {a.decision_note && a.status !== "pending" && (
                    <p className="mt-2 text-[12px] text-white/45 italic">Notiz: {a.decision_note}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================
 *  Dialog: Attendees (admin)
 * ========================================================= */
function AttendeesDialog({ ev, open, onOpenChange }) {
  const [list, setList] = useState(null);
  useEffect(() => {
    if (open && ev) {
      setList(null);
      axios.get(`${API}/events/${ev.id}/attendees`, { withCredentials: true }).then(({ data }) => setList(data)).catch(() => setList([]));
    }
  }, [open, ev]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none max-w-xl p-0" data-testid="attendees-dialog">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="font-outfit font-bold text-xl text-white tracking-tight">Gästeliste — {ev?.title}</DialogTitle>
            <DialogDescription className="text-[13px] text-white/45 mt-1">Nur für Admins sichtbar.</DialogDescription>
          </DialogHeader>
          <div className="mt-5 max-h-[60vh] overflow-y-auto space-y-2">
            {list === null && <p className="text-white/45 text-sm">Lade...</p>}
            {list && list.length === 0 && <p className="text-white/45 text-sm">Noch keine Anmeldungen.</p>}
            {list && list.map((a) => (
              <div key={a.user_id || a.email || `${a.name}-${a.registered_at}`} className="border border-white/10 p-3 bg-white/[0.02]">
                <div className="flex justify-between items-baseline">
                  <span className="font-outfit font-semibold text-white">{a.name}</span>
                  <span className="text-[11px] text-white/40">{a.company}</span>
                </div>
                <p className="text-[12px] text-white/50">{a.email}</p>
                {a.guests?.length > 0 && (
                  <div className="mt-2 pl-3 border-l-2 border-[#00e5ff]/30 space-y-1">
                    {a.guests.map((g) => (
                      <p key={`${g.email || ""}-${g.first_name}-${g.last_name}`} className="text-[12px] text-white/60">+ <span className="text-white/80">{g.first_name} {g.last_name}</span> <span className="text-white/35">· {g.email}</span></p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================
 *  Event Card
 * ========================================================= */
function EventCard({ ev, isAdmin, onRegister, onDelete, onShowAttendees, onShowApplications, onRefund, index }) {
  const ts = TYPE_STYLES[ev.event_type] || TYPE_STYLES["Vortrag"];
  const date = new Date(ev.date);
  const day = date.toLocaleDateString("de-DE", { day: "2-digit" });
  const month = date.toLocaleDateString("de-DE", { month: "short" });
  const weekday = date.toLocaleDateString("de-DE", { weekday: "long" });
  const isFull = ev.max_attendees && ev.attendee_count >= ev.max_attendees;
  const isPaid = ev.cost_type === "paid";
  const isDeepDive = ev.event_kind === "festival_deep_dive" || ev.requires_application;
  const isFestivalMain = ev.event_kind === "festival_main";
  const isSpeakerHere = ev.i_am_speaker;
  const canManage = isAdmin || isSpeakerHere;
  const app = ev.my_application;

  // Compute button label/variant
  let btnLabel, btnClass = "bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] hover:-translate-y-0.5";
  let btnIcon = <ArrowRight size={14} className="ml-1.5" />;
  if (ev.is_registered) {
    btnLabel = "Angemeldet";
    btnClass = "bg-transparent text-[#00e5ff] border border-[#00e5ff]/40 hover:bg-[#00e5ff]/10 hover:border-[#00e5ff]";
    btnIcon = <CheckCircle size={14} className="mr-1.5" />;
  } else if (ev.my_waitlist_position) {
    btnLabel = `Auf Warteliste (#${ev.my_waitlist_position})`;
    btnClass = "bg-transparent text-amber-400 border border-amber-400/40 hover:bg-amber-400/10";
    btnIcon = null;
  } else if (app?.status === "pending") {
    btnLabel = "Bewerbung läuft";
    btnClass = "bg-transparent text-[#a855f7] border border-[#a855f7]/40";
    btnIcon = <FileText size={14} className="mr-1.5" />;
  } else if (app?.status === "rejected") {
    btnLabel = "Abgelehnt";
    btnClass = "bg-transparent text-red-400 border border-red-400/40 cursor-not-allowed";
    btnIcon = null;
  } else if (isPaid) {
    btnLabel = `Anmelden · ${formatEUR(ev.price_cents)}`;
    btnClass = "bg-amber-400 text-black hover:bg-amber-400 hover:shadow-[0_0_32px_-4px_rgba(251,191,36,0.7)] hover:-translate-y-0.5";
    btnIcon = <CreditCard size={14} className="ml-1.5" />;
  } else if (isDeepDive) {
    btnLabel = "Bewerben";
    btnClass = "bg-[#a855f7] text-white hover:bg-[#a855f7] hover:shadow-[0_0_32px_-4px_rgba(168,85,247,0.6)] hover:-translate-y-0.5";
    btnIcon = <FileText size={14} className="ml-1.5" />;
  } else if (isFull) {
    btnLabel = "Auf Warteliste";
    btnClass = "bg-transparent text-amber-300 border border-amber-400/50 hover:bg-amber-400/10";
  } else {
    btnLabel = "Anmelden";
  }

  return (
    <div className="group relative event-card-reveal" style={{ animationDelay: `${Math.min(index * 80, 480)}ms` }} data-testid={`event-card-${ev.id}`}>
      <div className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: `linear-gradient(135deg, ${ts.hex}44 0%, transparent 40%, transparent 60%, ${ts.hex}22 100%)` }} />
      <div className="relative bg-gradient-to-br from-white/[0.015] to-transparent border border-white/[0.07] transition-all duration-300 group-hover:bg-white/[0.025]" style={{ borderLeft: `3px solid ${ts.hex}` }}>
        {ev.flyer_url && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}${ev.flyer_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-black flex items-center justify-center cursor-zoom-in transition-opacity hover:opacity-95 py-2"
            aria-label={`Flyer ${ev.title} in voller Größe öffnen`}
          >
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${ev.flyer_url}`}
              alt={ev.title}
              className="max-h-[640px] w-auto max-w-full object-contain"
              loading="lazy"
              data-testid={`event-flyer-${ev.id}`}
            />
          </a>
        )}
        <div className="p-5 sm:p-6 relative">
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: `radial-gradient(circle at 85% 0%, ${ts.hex}0a 0%, transparent 55%)` }} />
          <div className="relative flex flex-col md:flex-row md:items-center gap-5">
            <div className="flex md:flex-col items-center md:items-start gap-3 md:gap-0 md:w-24 md:shrink-0 md:border-r md:border-white/[0.06] md:pr-5">
              <div className="flex flex-col items-center leading-none">
                <span className="font-outfit font-black text-3xl sm:text-4xl tracking-tighter" style={{ color: ts.hex }}>{day}</span>
                <span className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-outfit font-semibold mt-1">{month}</span>
              </div>
              <span className="text-[11px] uppercase tracking-[0.18em] text-white/30 font-outfit md:mt-2">{weekday}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold border" style={{ color: ts.hex, borderColor: `${ts.hex}40`, background: `${ts.hex}0d` }}>
                  {ts.label || ev.event_type}
                </span>
                {isPaid && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-bold border border-amber-400/50 text-amber-300 bg-amber-400/10" data-testid={`price-badge-${ev.id}`}>
                    <CreditCard size={10} /> {formatEUR(ev.price_cents)}
                  </span>
                )}
                {isDeepDive && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-bold border border-[#a855f7]/50 text-[#a855f7] bg-[#a855f7]/10">
                    <FileText size={10} /> Bewerbung
                  </span>
                )}
                {isFestivalMain && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-bold border border-[#00e5ff]/50 text-[#00e5ff] bg-[#00e5ff]/10">
                    <Sparkles size={10} /> Festival
                  </span>
                )}
                {isSpeakerHere && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-bold border border-[#a855f7]/60 text-[#a855f7] bg-[#a855f7]/15">
                    <Mic size={10} /> Speaker
                  </span>
                )}
                <span className="flex items-center gap-1 text-[12px] text-white/50"><Clock size={11} />{ev.time} Uhr</span>
              </div>
              <h3 className="font-outfit font-bold text-lg sm:text-xl text-white tracking-tight leading-snug mb-2">{ev.title}</h3>
              <p className="text-[15px] text-white/55 mb-3 whitespace-pre-wrap line-clamp-3 leading-relaxed">{ev.description}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-white/45">
                <span className="flex items-center gap-1.5"><MapPin size={12} />{ev.location}</span>
                <span className="flex items-center gap-1.5"><Users size={12} />{ev.attendee_count}{ev.max_attendees ? `/${ev.max_attendees}` : ""} Teilnehmer</span>
                {ev.waitlist_count > 0 && <span className="text-amber-400/80">{ev.waitlist_count} auf Warteliste</span>}
                {isFull && !ev.is_registered && ev.my_waitlist_position == null && !isPaid && !isDeepDive && <span className="text-red-400/80 font-semibold">Ausgebucht · Warteliste möglich</span>}
                {ev.is_registered && ev.my_guests?.length > 0 && (
                  <span className="text-[#00e5ff]/80">+ {ev.my_guests.length} Gast{ev.my_guests.length !== 1 ? "e" : ""}</span>
                )}
                {ev.is_registered && ev.i_am_paid && (
                  <span className="flex items-center gap-1 text-[#2ecc71]/80"><ShieldCheck size={11} /> bezahlt</span>
                )}
              </div>
            </div>
            <div className="flex md:flex-col items-stretch md:items-end gap-2 shrink-0">
              {isFestivalMain && (
                <RouterLink to={`/events/festival/${ev.id}`} data-testid={`festival-link-${ev.id}`}
                  className="inline-flex items-center justify-center gap-1.5 bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 rounded-none h-11 px-5 font-outfit font-semibold text-[13px] tracking-wide transition-all">
                  <Sparkles size={14} /> Zum Festival
                  <ArrowRight size={14} />
                </RouterLink>
              )}
              {!isFestivalMain && (
                <Button
                  onClick={() => onRegister(ev)}
                  disabled={app?.status === "rejected"}
                  data-testid={`register-event-${ev.id}`}
                  className={`rounded-none h-11 px-5 font-outfit font-semibold text-[13px] tracking-wide transition-all duration-300 ${btnClass}`}
                >
                  {btnIcon && typeof btnIcon === "object" && btnIcon.props.className?.includes("mr-") ? btnIcon : null}
                  {btnLabel}
                  {btnIcon && typeof btnIcon === "object" && btnIcon.props.className?.includes("ml-") ? btnIcon : null}
                </Button>
              )}
              {/* Refund button for paid registered */}
              {ev.is_registered && ev.i_am_paid && (
                <button onClick={() => onRefund(ev)} data-testid={`refund-event-${ev.id}`} className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-amber-400/70 hover:text-amber-400 transition-colors font-outfit font-medium px-2 justify-center">
                  <X size={11} /><span>Stornieren &amp; erstatten</span>
                </button>
              )}
              {/* ICS Kalender-Download (für alle sichtbar, v.a. wenn angemeldet) */}
              {!isFestivalMain && (
                <a href={`${API}/events/${ev.id}/ics`} download data-testid={`ics-download-${ev.id}`}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/45 hover:text-[#00e5ff] transition-colors font-outfit font-medium px-2 justify-center">
                  <Download size={11} /><span>In Kalender</span>
                </a>
              )}
              {canManage && isDeepDive && (
                <button onClick={() => onShowApplications(ev)} data-testid={`applications-event-${ev.id}`} className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#a855f7]/80 hover:text-[#a855f7] transition-colors font-outfit font-medium px-2 justify-center">
                  <Mic size={11} /><span>Bewerbungen</span>
                </button>
              )}
              {isAdmin && (
                <>
                  <button onClick={() => onShowAttendees(ev)} data-testid={`attendees-event-${ev.id}`} className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/45 hover:text-[#00e5ff] transition-colors font-outfit font-medium px-2 justify-center">
                    <List size={11} /><span>Liste</span>
                  </button>
                  <button onClick={() => onDelete(ev.id)} data-testid={"delete-event-" + ev.id} className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-red-400/60 hover:text-red-400 transition-colors font-outfit font-medium px-2 justify-center">
                    <Trash2 size={11} /><span>Löschen</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
 *  Main page
 * ========================================================= */
const EMPTY_FORM = {
  title: "", description: "", date: "", time: "", location: "",
  event_type: "Vortrag", max_attendees: "", flyer_url: "",
  cost_type: "free", price_eur: "",
  event_kind: "standard", festival_parent_id: "", requires_application: false,
};

export default function EventsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [regEvent, setRegEvent] = useState(null);
  const [paidEvent, setPaidEvent] = useState(null);
  const [applyEvent, setApplyEvent] = useState(null);
  const [attendeesEvent, setAttendeesEvent] = useState(null);
  const [manageEvent, setManageEvent] = useState(null);
  const [filter, setFilter] = useState("all");  // all | upcoming | mine | paid | festival
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestForm, setSuggestForm] = useState({ title: "", description: "", desired_format: "", desired_date: "" });
  const [suggestSubmitting, setSuggestSubmitting] = useState(false);
  const [suggestErr, setSuggestErr] = useState("");
  const [suggestSent, setSuggestSent] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(data);
      setError("");
    } catch (err) {
      console.error("Failed to fetch events:", err);
      setError("Events konnten nicht geladen werden.");
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  /* ---- Stripe redirect handler ---------------------- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("payment_session");
    const cancelled = params.get("payment_cancelled");
    if (cancelled) {
      setNotice("Zahlung abgebrochen. Du wurdest nicht angemeldet.");
      window.history.replaceState({}, "", "/events");
      return;
    }
    if (!sessionId) return;
    setNotice("Zahlung wird geprüft...");
    let tries = 0;
    const maxTries = 10;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (data.payment_status === "paid") {
          setNotice("Zahlung erfolgreich! Rechnung folgt per E-Mail und liegt in deinem Profil.");
          await fetchEvents();
          window.history.replaceState({}, "", "/events");
          return;
        }
        if (tries < maxTries) {
          setTimeout(poll, 2000);
        } else {
          setNotice("Zahlungsstatus konnte nicht bestätigt werden. Bitte prüfe dein Profil in wenigen Minuten.");
          window.history.replaceState({}, "", "/events");
        }
      } catch (e) {
        if (tries < maxTries) setTimeout(poll, 2000);
        else { setNotice("Zahlungsstatus konnte nicht geprüft werden."); window.history.replaceState({}, "", "/events"); }
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const festivalMains = useMemo(() => events.filter(e => e.event_kind === "festival_main"), [events]);

  const filteredEvents = useMemo(() => {
    const now = Date.now();
    switch (filter) {
      case "upcoming":
        return events.filter(e => new Date(e.date).getTime() >= now - 86400000);
      case "mine":
        return events.filter(e => e.is_registered || e.my_waitlist_position || e.my_application?.status);
      case "paid":
        return events.filter(e => e.cost_type === "paid");
      case "festival":
        return events.filter(e => e.event_kind === "festival_main" || e.event_kind === "festival_deep_dive");
      default:
        return events;
    }
  }, [events, filter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); setError("");
    try {
      const payload = {
        title: form.title,
        description: form.description,
        date: form.date,
        time: form.time,
        location: form.location,
        event_type: form.event_type,
        max_attendees: form.max_attendees ? parseInt(form.max_attendees, 10) : null,
        flyer_url: form.flyer_url,
        cost_type: form.cost_type,
        price_cents: form.cost_type === "paid" ? Math.round(parseFloat(form.price_eur || "0") * 100) : 0,
        event_kind: form.event_kind,
        festival_parent_id: form.event_kind === "festival_deep_dive" ? (form.festival_parent_id || null) : null,
        requires_application: !!form.requires_application || form.event_kind === "festival_deep_dive",
      };
      await axios.post(`${API}/events`, payload, { withCredentials: true });
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      fetchEvents();
    } catch (err) {
      setError(err?.response?.data?.detail || "Event konnte nicht erstellt werden.");
    } finally { setIsSubmitting(false); }
  };

  const handleRegisterClick = useCallback(async (ev) => {
    // Already registered OR on waitlist → one-click cancel (for free events only)
    if (ev.is_registered && !ev.i_am_paid) {
      try {
        await axios.post(`${API}/events/${ev.id}/register`, { accepted_terms: true }, { withCredentials: true });
        fetchEvents();
      } catch (err) {
        setError(err?.response?.data?.detail || "Abmeldung fehlgeschlagen.");
      }
      return;
    }
    if (ev.my_waitlist_position) {
      try {
        await axios.post(`${API}/events/${ev.id}/register`, { accepted_terms: true }, { withCredentials: true });
        fetchEvents();
      } catch (err) {
        setError(err?.response?.data?.detail || "Abmeldung fehlgeschlagen.");
      }
      return;
    }
    if (ev.cost_type === "paid") { setPaidEvent(ev); return; }
    if (ev.requires_application) {
      if (ev.my_application?.status === "pending" || ev.my_application?.status === "rejected") return;
      setApplyEvent(ev); return;
    }
    setRegEvent(ev);
  }, [fetchEvents]);

  const confirmFreeRegister = async (guests, accepted_terms) => {
    await axios.post(`${API}/events/${regEvent.id}/register`, { guests, accepted_terms }, { withCredentials: true });
    setRegEvent(null);
    fetchEvents();
  };

  const handleRefund = async (ev) => {
    if (!window.confirm("Anmeldung stornieren & Betrag erstatten?\n\n100 %-Rückerstattung nur bis 5 Tage vor dem Event möglich.")) return;
    try {
      const { data } = await axios.delete(`${API}/payments/registration/${ev.id}`, { withCredentials: true });
      setNotice(`Rückerstattung ausgelöst: ${(Number(data.amount) || 0).toFixed(2)} €`);
      fetchEvents();
    } catch (err) {
      setError(err?.response?.data?.detail || "Rückerstattung fehlgeschlagen.");
    }
  };

  const handleDelete = useCallback(async (eventId) => {
    if (!window.confirm("Event wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      fetchEvents();
    } catch { setError("Event konnte nicht gelöscht werden."); }
  }, [fetchEvents]);

  const handleSuggest = async (e) => {
    e.preventDefault();
    setSuggestSubmitting(true); setSuggestErr(""); setSuggestSent(false);
    try {
      await axios.post(`${API}/events/suggest`, suggestForm, { withCredentials: true });
      setSuggestSent(true);
      setSuggestForm({ title: "", description: "", desired_format: "", desired_date: "" });
    } catch (err) {
      setSuggestErr(err?.response?.data?.detail || "Senden fehlgeschlagen.");
    } finally { setSuggestSubmitting(false); }
  };

  const isAdmin = user?.role === "admin";
  const upcomingCount = useMemo(() => events.filter((e) => new Date(e.date) >= new Date(Date.now() - 86400000)).length, [events]);
  const registeredCount = useMemo(() => events.filter((e) => e.is_registered).length, [events]);

  return (
    <div className="min-h-screen bg-black text-white antialiased selection:bg-[#00e5ff]/30 relative overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[45%] h-[55%] rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.06) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[45%] h-[55%] rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.05) 0%, transparent 70%)' }} />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-px bg-[#00e5ff]" />
                <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#00e5ff]/80 font-semibold">Veranstaltungen</span>
              </div>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-white mb-3" data-testid="events-heading">
                KI- <span className="text-[#00e5ff]">Events</span>
              </h1>
              <p className="text-base sm:text-lg text-white/50 max-w-lg leading-relaxed">
                Themenabende, Deep Dives, Festivals und Workshops — regional im Illertal.
              </p>
            </div>
            {isAdmin ? (
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-event-btn" className="group/btn relative overflow-hidden bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none px-5 h-11 font-outfit font-semibold text-[13px] tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] shrink-0">
                    <Plus size={15} className="mr-1.5" />Neues Event
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-transparent border-0 text-white rounded-none max-w-lg p-0 shadow-none max-h-[90vh] overflow-y-auto" data-testid="create-event-dialog">
                  <div className="relative">
                    <div className="absolute -inset-px pointer-events-none" style={{ background: `linear-gradient(135deg, ${ACCENT}40 0%, transparent 50%, ${ACCENT}20 100%)` }} />
                    <div className="relative bg-[#0a0a0a] border border-white/10 p-6 sm:p-8">
                      <DialogHeader>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-px bg-[#00e5ff]/60" />
                          <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit font-semibold">Erstellen</span>
                        </div>
                        <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">Neues Event</DialogTitle>
                        <DialogDescription className="text-[13px] text-white/45 mt-1">Erstelle eine neue Veranstaltung für die Community. Alle Mitglieder erhalten eine E-Mail.</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreate} className="space-y-4 mt-5">
                        <div>
                          <FieldLabel>Titel *</FieldLabel>
                          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" className={inputCls} />
                        </div>
                        <div>
                          <FieldLabel>Beschreibung *</FieldLabel>
                          <Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-desc-input" className="mt-2 bg-black border-white/10 text-white rounded-none focus:border-[#00e5ff]/60 focus:ring-0 min-h-[100px] text-[14px]" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <FieldLabel>Datum *</FieldLabel>
                            <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date-input" className={inputCls} />
                          </div>
                          <div>
                            <FieldLabel>Uhrzeit *</FieldLabel>
                            <Input type="time" required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="event-time-input" className={inputCls} />
                          </div>
                        </div>
                        <div>
                          <FieldLabel>Ort *</FieldLabel>
                          <Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="event-location-input" className={inputCls} placeholder="z.B. teamWERK Erolzheim" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <FieldLabel>Typ</FieldLabel>
                            <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[#0a0a0a] border-white/10 text-white rounded-none">
                                {Object.keys(TYPE_STYLES).map((t) => <SelectItem key={t} value={t} className="rounded-none hover:bg-white/5 focus:bg-white/5 focus:text-white">{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <FieldLabel>Max. Teilnehmer</FieldLabel>
                            <Input type="number" value={form.max_attendees} onChange={(e) => setForm({ ...form, max_attendees: e.target.value })} className={inputCls} placeholder="unbegrenzt" />
                          </div>
                        </div>

                        {/* Format */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <FieldLabel>Format</FieldLabel>
                            <Select value={form.event_kind} onValueChange={(v) => setForm({ ...form, event_kind: v, requires_application: v === "festival_deep_dive" ? true : form.requires_application })}>
                              <SelectTrigger className={inputCls} data-testid="event-kind-select"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[#0a0a0a] border-white/10 text-white rounded-none">
                                <SelectItem value="standard" className="rounded-none hover:bg-white/5 focus:bg-white/5 focus:text-white">Standard</SelectItem>
                                <SelectItem value="festival_main" className="rounded-none hover:bg-white/5 focus:bg-white/5 focus:text-white">Festival · Hauptevent</SelectItem>
                                <SelectItem value="festival_deep_dive" className="rounded-none hover:bg-white/5 focus:bg-white/5 focus:text-white">Festival · Deep Dive</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {form.event_kind === "festival_deep_dive" && (
                            <div>
                              <FieldLabel>Gehört zu Festival</FieldLabel>
                              <Select value={form.festival_parent_id} onValueChange={(v) => setForm({ ...form, festival_parent_id: v })}>
                                <SelectTrigger className={inputCls} data-testid="festival-parent-select"><SelectValue placeholder="Festival wählen" /></SelectTrigger>
                                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white rounded-none">
                                  {festivalMains.length === 0 && <SelectItem value="__none__" disabled>Kein Festival gefunden</SelectItem>}
                                  {festivalMains.map((m) => <SelectItem key={m.id} value={m.id} className="rounded-none hover:bg-white/5 focus:bg-white/5 focus:text-white">{m.title}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>

                        {/* Cost */}
                        <div className="border border-white/10 p-3 bg-white/[0.015] space-y-3">
                          <FieldLabel>Kosten</FieldLabel>
                          <div className="flex gap-2">
                            {[{ v: "free", label: "Kostenlos" }, { v: "paid", label: "Kostenpflichtig" }].map((o) => (
                              <button type="button" key={o.v} onClick={() => setForm({ ...form, cost_type: o.v })}
                                data-testid={`cost-type-${o.v}`}
                                className={`flex-1 h-10 border text-[12px] uppercase tracking-[0.18em] font-outfit font-semibold transition-colors ${form.cost_type === o.v ? "bg-[#00e5ff]/10 border-[#00e5ff]/60 text-[#00e5ff]" : "border-white/10 text-white/60 hover:border-white/30"}`}>
                                {o.label}
                              </button>
                            ))}
                          </div>
                          {form.cost_type === "paid" && (
                            <div>
                              <FieldLabel>Preis pro Platz (EUR, inkl. USt.) *</FieldLabel>
                              <Input required={form.cost_type === "paid"} type="number" step="0.01" min="0.5"
                                value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
                                data-testid="event-price-input" className={inputCls} placeholder="z.B. 49,00" />
                            </div>
                          )}
                        </div>

                        {/* Application toggle (only when not paid and not deep-dive already forced) */}
                        {form.event_kind !== "festival_deep_dive" && (
                          <label className="flex items-start gap-3 p-3 border border-white/10 bg-white/[0.015] cursor-pointer" data-testid="require-application-label">
                            <input type="checkbox" checked={form.requires_application} onChange={(e) => setForm({ ...form, requires_application: e.target.checked })} className="mt-1 w-4 h-4 accent-[#a855f7] cursor-pointer" data-testid="require-application-checkbox" />
                            <div>
                              <div className="text-[13px] text-white font-outfit font-semibold">Bewerbung nötig</div>
                              <p className="text-[12px] text-white/45 mt-0.5">Interessenten schreiben eine Motivation. Speaker/Admin entscheiden über Zulassung.</p>
                            </div>
                          </label>
                        )}

                        <FlyerUploader value={form.flyer_url} onChange={(url) => setForm({ ...form, flyer_url: url })} />
                        <Button type="submit" disabled={isSubmitting} data-testid="submit-event-btn" className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 text-[14px] tracking-wide transition-all">
                          {isSubmitting ? "..." : "Event erstellen & Community benachrichtigen"}
                        </Button>
                      </form>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="suggest-event-btn" className="bg-transparent text-[#00e5ff] border border-[#00e5ff]/50 hover:bg-[#00e5ff]/10 hover:border-[#00e5ff] rounded-none px-5 h-11 font-outfit font-semibold text-[13px] tracking-wide transition-all shrink-0">
                    <Sparkles size={15} className="mr-1.5" />Veranstaltung vorschlagen
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none max-w-lg p-6 sm:p-8" data-testid="suggest-event-dialog">
                  <DialogHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-px bg-[#00e5ff]/60" />
                      <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit font-semibold">Vorschlag an die Admins</span>
                    </div>
                    <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight">Veranstaltung vorschlagen</DialogTitle>
                    <DialogDescription className="text-[13px] text-white/45 mt-1">Du hast eine Idee? Schreib uns — wir schauen, ob wir das umsetzen können.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSuggest} className="space-y-4 mt-5">
                    <div>
                      <FieldLabel>Titel *</FieldLabel>
                      <Input required minLength={3} maxLength={120} value={suggestForm.title} onChange={(e) => setSuggestForm({ ...suggestForm, title: e.target.value })} data-testid="suggest-title-input" className={inputCls} placeholder="z.B. Deep Dive Prompt-Engineering" />
                    </div>
                    <div>
                      <FieldLabel>Beschreibung / Inhalt *</FieldLabel>
                      <Textarea required minLength={10} maxLength={1500} value={suggestForm.description} onChange={(e) => setSuggestForm({ ...suggestForm, description: e.target.value })} data-testid="suggest-description-input" className="mt-2 bg-black border-white/10 text-white rounded-none focus:border-[#00e5ff]/60 focus:ring-0 min-h-[120px] text-[14px]" placeholder="Was soll passieren? Für wen? Warum jetzt?" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <FieldLabel>Format (optional)</FieldLabel>
                        <Input value={suggestForm.desired_format} onChange={(e) => setSuggestForm({ ...suggestForm, desired_format: e.target.value })} data-testid="suggest-format-input" className={inputCls} placeholder="z.B. Workshop" />
                      </div>
                      <div>
                        <FieldLabel>Wunschtermin (optional)</FieldLabel>
                        <Input value={suggestForm.desired_date} onChange={(e) => setSuggestForm({ ...suggestForm, desired_date: e.target.value })} data-testid="suggest-date-input" className={inputCls} placeholder="z.B. Juli 2026" />
                      </div>
                    </div>
                    {suggestErr && <p className="text-[13px] text-red-400" data-testid="suggest-error">{suggestErr}</p>}
                    {suggestSent && <p className="text-[13px] text-[#2ecc71]" data-testid="suggest-sent">Danke! Die Admins wurden benachrichtigt — du bekommst eine Antwort per E-Mail.</p>}
                    <Button type="submit" disabled={suggestSubmitting || suggestSent} data-testid="suggest-submit-btn" className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 text-[14px] tracking-wide transition-all">
                      {suggestSubmitting ? "..." : suggestSent ? "Gesendet ✓" : "Vorschlag senden"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {notice && (
            <div className="mb-6 border-l-2 border-[#00e5ff] bg-[#00e5ff]/5 p-4 text-[13px] text-[#00e5ff]/90" data-testid="events-notice">
              {notice}
              <button onClick={() => setNotice("")} className="ml-3 text-white/40 hover:text-white">×</button>
            </div>
          )}

          <div className="grid grid-cols-3 gap-px bg-white/[0.05] mb-10 border border-white/[0.06]">
            {[
              { num: events.length, label: "Events gesamt", sub: "veröffentlicht", color: ACCENT, icon: Calendar },
              { num: upcomingCount, label: "Kommende", sub: "in den nächsten Tagen", color: "#2ecc71", icon: Sparkles },
              { num: registeredCount, label: "Angemeldet", sub: "deine Teilnahmen", color: "#a855f7", icon: CheckCircle },
            ].map((s) => (
              <div key={s.label} className="bg-black px-4 sm:px-6 py-5 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <s.icon size={15} className="text-white/30" strokeWidth={1.5} />
                </div>
                <div className="font-outfit font-black text-3xl sm:text-4xl tracking-tighter" style={{ color: s.color }}>{s.num}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/55 font-outfit font-medium">{s.label}</div>
                <div className="text-[12px] text-white/30 mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>

          {events.length === 0 ? (
            <div className="relative text-center py-24 border border-white/[0.06] bg-gradient-to-br from-white/[0.015] to-transparent overflow-hidden" data-testid="no-events">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-[#00e5ff]/[0.04] blur-3xl pointer-events-none" />
              <div className="relative">
                <Calendar size={32} className="text-[#00e5ff]/50 mx-auto mb-4" strokeWidth={1.3} />
                <p className="text-white/80 text-lg font-outfit font-semibold tracking-tight">Noch keine Events geplant</p>
                <p className="text-[15px] text-white/45 mt-1.5 max-w-sm mx-auto">Sobald ein Admin ein Event anlegt, siehst du es hier.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Filter-Pills */}
              <div className="flex flex-wrap gap-2 mb-5" data-testid="events-filter-bar">
                {[
                  { v: "all", label: "Alle", n: events.length },
                  { v: "upcoming", label: "Kommende", n: upcomingCount },
                  { v: "mine", label: "Meine", n: registeredCount },
                  { v: "festival", label: "Festival", n: events.filter(e => e.event_kind?.startsWith("festival_")).length },
                  { v: "paid", label: "Kostenpflichtig", n: events.filter(e => e.cost_type === "paid").length },
                ].map((p) => (
                  <button
                    key={p.v}
                    onClick={() => setFilter(p.v)}
                    data-testid={`events-filter-${p.v}`}
                    className={`h-9 px-3.5 text-[12px] uppercase tracking-[0.18em] font-outfit font-semibold transition-colors border rounded-none inline-flex items-center gap-2 ${
                      filter === p.v
                        ? "bg-[#00e5ff]/10 border-[#00e5ff]/60 text-[#00e5ff]"
                        : "border-white/10 text-white/55 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {p.label}
                    <span className={`text-[10px] ${filter === p.v ? "text-[#00e5ff]/80" : "text-white/35"}`}>{p.n}</span>
                  </button>
                ))}
              </div>

              {filteredEvents.length === 0 ? (
                <div className="text-center py-16 border border-white/[0.06] bg-white/[0.01]" data-testid="events-filter-empty">
                  <p className="text-white/60 text-[14px] font-outfit">Keine Events in dieser Ansicht.</p>
                </div>
              ) : (
                <div className="space-y-4 sm:space-y-5" data-testid="events-list">
                  {filteredEvents.map((ev, idx) => (
                    <EventCard
                      key={ev.id} ev={ev} isAdmin={isAdmin}
                      onRegister={handleRegisterClick}
                      onDelete={handleDelete}
                      onShowAttendees={setAttendeesEvent}
                      onShowApplications={setManageEvent}
                      onRefund={handleRefund}
                      index={idx}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {error && <p className="mt-6 text-red-400/80 text-[13px]" data-testid="events-error">{error}</p>}
        </div>
      </div>

      <RegisterDialog ev={regEvent} open={!!regEvent} onOpenChange={(v) => !v && setRegEvent(null)} onConfirm={confirmFreeRegister} user={user} />
      <PaidCheckoutDialog ev={paidEvent} open={!!paidEvent} onOpenChange={(v) => !v && setPaidEvent(null)} user={user} />
      <ApplicationDialog ev={applyEvent} open={!!applyEvent} onOpenChange={(v) => !v && setApplyEvent(null)} onDone={() => { setApplyEvent(null); fetchEvents(); }} />
      <AttendeesDialog ev={attendeesEvent} open={!!attendeesEvent} onOpenChange={(v) => !v && setAttendeesEvent(null)} />
      <ApplicationsManagerDialog ev={manageEvent} open={!!manageEvent} onOpenChange={(v) => !v && setManageEvent(null)} onChanged={fetchEvents} />

      <style>{`
        .event-card-reveal { opacity: 0; transform: translateY(16px); animation: card-slide 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes card-slide { to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) { .event-card-reveal { animation: none; opacity: 1; transform: none; } }
      `}</style>
    </div>
  );
}
